import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaArrowLeft, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { UniversityAuthService } from '../../services/UniversityAuthService';
import { useSnackBar } from '../../hooks/useSnackBar';

const UniversityForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [errors, setErrors] = useState({});
  
  const navigate = useNavigate();
  const enqMsg = useSnackBar();

  // Check if user is already authenticated and redirect to dashboard
  useEffect(() => {
    const checkAuthAndRedirect = () => {
      if (UniversityAuthService.isAuthenticated()) {
        // User is already logged in, redirect to dashboard
        navigate('/university/dashboard', { replace: true });
      } else {
        // User is not authenticated, show forgot password form
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate]);

  const handleChange = (e) => {
    const { value } = e.target;
    setEmail(value);
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await UniversityAuthService.forgotPassword(email);
      
      if (response.success) {
        setIsEmailSent(true);
        enqMsg('Password reset email sent! Please check your inbox.', 'success');
      } else {
        enqMsg(response.message || 'Failed to send password reset email', 'error');
      }
    } catch (error) {
      enqMsg(error.message || 'An error occurred. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFE5E3] via-white to-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/25">
              <FaCheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Please click the link in the email to reset your password. The link will expire in 1 hour.
            </p>
          </div>
          
          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100 text-center">
            <p className="text-sm text-gray-600 mb-4">
              Didn't receive the email? Check your spam folder or
            </p>
            <button
              onClick={() => setIsEmailSent(false)}
              className="text-[#DB4F40] hover:text-[#C64537] font-medium transition-colors duration-200"
            >
              try again
            </button>
          </div>
          
          <div className="text-center">
            <Link
              to="/university/login"
              className="inline-flex items-center text-sm text-gray-500 hover:text-[#DB4F40] transition-colors duration-200"
            >
              <FaArrowLeft className="mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFE5E3] via-white to-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center shadow-lg shadow-[#DB4F40]/25">
              <FaEnvelope className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-[#DB4F40] to-[#C64537] bg-clip-text text-transparent">
              Checking Authentication
            </h2>
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DB4F40]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFE5E3] via-white to-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center shadow-lg shadow-[#DB4F40]/25">
            <FaEnvelope className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Forgot Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        {/* Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your university email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] hover:from-[#C64537] hover:to-[#B73E31] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DB4F40] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-[#DB4F40]/25"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Sending Reset Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>

            {/* Links */}
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <Link
                  to="/university/login"
                  className="font-medium text-[#DB4F40] hover:text-[#C64537] transition-colors duration-200"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-[#DB4F40] transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UniversityForgotPassword;
